import { constructUrl } from "./apiConfig";
import makeRequest from "./apiRequest";
import {
  CheckMfaStatusResponseSchema,
  SendMfaChallengeResponseSchema,
  SendPasscodeResponseSchema,
  MfaVerificationResult,
} from "../types/mfa";
import { validateApiResponse } from "../types/validators";

/**
 * Send a 6-digit code to backend
 */
export const sendPasscode = async (
  userId: string,
  factorType: string,
  factorId: string,
  passcode: string,
  sessionId: string
): Promise<{ sessionId: string | null; errorMsg: string | undefined }> => {
  console.log(`mfa: sendPasscode: factorType= ${factorType}`);

  const endpoint = "/verify_response";
  const url = constructUrl(endpoint);
  const response = await makeRequest(
    url,
    "POST",
    {
      type: factorType,
      factorid: factorId,
      userid: userId,
      passcode: passcode,
      sessionid: sessionId,
    },
    {},
    {}
  );
  if (response.errorType !== 0) {
    return { sessionId: null, errorMsg: response.errorMessage };
  }

  try {
    const data = validateApiResponse(
      SendPasscodeResponseSchema.shape.data,
      { errorType: 0, data: response.data },
      "sendPasscode"
    );
    return { sessionId: data.session_id ?? null, errorMsg: undefined };
  } catch {
    return {
      sessionId: null,
      errorMsg: "MFA passcode response failed validation",
    };
  }
};

/*
 * Send Okta Push or other MFA challenge
 */
export const sendMfaChallenge = async (
  factorType: string,
  userId: string,
  factorId: string
): Promise<{ data: unknown | null; error: string | undefined }> => {
  console.log(`mfa: sendMfaChallenge: factorType= ${factorType}`);

  const endpoint = "/trigger_challenge";
  const url = constructUrl(endpoint);
  const response = await makeRequest(
    url,
    "POST",
    { type: factorType, factorid: factorId, userid: userId },
    {},
    {}
  );
  if (response.errorType !== 0) {
    return {
      data: null,
      error: response.errorMessage,
    };
  }

  try {
    const validatedData = validateApiResponse(
      SendMfaChallengeResponseSchema.shape.data,
      { errorType: 0, data: response.data },
      'sendMfaChallenge'
    );
    return {
      data: validatedData,
      error: undefined,
    };
  } catch {
    return {
      data: null,
      error: "MFA challenge response failed validation",
    };
  }
};

/*
 * Get MFA status from the Push
 */
export const checkMfaStatus = async (
  factorType: string,
  userId: string,
  factorId: string,
  sessionId: string
): Promise<MfaVerificationResult> => {
  console.log(`mfa: checkMfaStatus: factorType= ${factorType}`);

  const endpoint = "/verify_response";
  const url = constructUrl(endpoint);
  const response = await makeRequest(
    url,
    "POST",
    {
      type: factorType,
      factorid: factorId,
      userid: userId,
      sessionid: sessionId,
    },
    {},
    {}
  );

  if (response.errorType !== 0) {
    const errorMessage = response.errorMessage ?? "Unknown error";
    if (errorMessage.includes("Invalid session")) {
      return {
        status: "timeout",
        errorMsg: "Timed out waiting for user response.",
      };
    } else {
      return {
        status: "error",
        errorMsg: errorMessage,
      };
    }
  }

  let next = "";
  try {
    const data = validateApiResponse(
      CheckMfaStatusResponseSchema.shape.data,
      { errorType: 0, data: response.data },
      "checkMfaStatus"
    );
    next = data.next ?? "";
  } catch {
    return {
      status: "error",
      errorMsg: "MFA status response failed validation",
    };
  }

  if (next.includes("sendmessage")) {
    return {
      status: "accepted",
    };
  } else if (next.includes("verify_response")) {
    return {
      status: "waiting",
    };
  } else {
    return {
      status: "error",
      errorMsg: `*** Unexpected response ${next}`,
    };
  }
};
